import { createSelector } from '@reduxjs/toolkit';

import { type RootState } from '../store';
import { POGOS } from '../../features/intervention/helpers/pogo';
import { InterventionType } from '../../apis/interventions/intervention.type';
import { InterventionStatus } from '../../business/interventionButtonMenu.const';
import { selectStoreId } from './userInfo';
import { selectProduct } from './catalog';
import { useSelectCategoriesWithBattery, useSelectFeatureSettings } from '../../apis/startup/startup.selectors';

export const selectInterventionNatures = (state: RootState) => state.intervention.natures;
export const selectServices = createSelector(selectInterventionNatures, services => services.flatMap(nature => nature.services));
export const selectSpareParts = createSelector(selectInterventionNatures, parts => parts.flatMap(nature => nature.spareParts));

export const selectInterventionSpareParts = selectSpareParts;
export const selectInterventionServices = selectServices;

export const selectRealizationSiteName = (state: RootState) => state.intervention.realizationSite?.name;
export const selectCreationSiteName = (state: RootState) => state.intervention.creationSite?.name;

export const selectIsInternalIntervention = (state: RootState) => state.intervention.interventionType === InterventionType.internal;

export const selectIsExpressIntervention = (state: RootState) => state.intervention.interventionType === InterventionType.express;

export const selectIsInsuranceIntervention = (state: RootState) => state.intervention.interventionType === InterventionType.insurance;

export type ActionsLabelsState = ReturnType<typeof selectInterventionButtonMenuState>;

export const selectOnlyWepIntervention = (state: RootState) => state.intervention.interventionType === InterventionType.web;

export const getIsInterventionWarrantyOnly = (state: RootState) =>
  state.intervention.interventionType === InterventionType.homeService || selectIsInternalIntervention(state);

export const selectInterventionStateId = (state: RootState) => state.intervention.footer.state.stateId;

export const selectIsInterventionStarted = (state: RootState) => selectInterventionStateId(state) !== InterventionStatus.notStarted;

export const isExistingIntervention = (state: RootState) => selectInterventionStateId(state) !== InterventionStatus.inCreation;

export const selectInterventionDocNumber = (state: RootState) => state.intervention.footer.docNumber;

export const selectIsInCreation = (state: RootState) =>
  selectInterventionStateId(state) === InterventionStatus.inCreation || !selectInterventionStateId(state);

export const selectIsFromTraceability = (state: RootState) => state.intervention.fromTraceability;

export const selectIsInterventionStateId = createSelector(
  selectInterventionStateId,
  stateId => (stateIdToCompare: InterventionStatus) => stateId === stateIdToCompare
);

export const selectHasEntityWithWarranty = createSelector(selectInterventionNatures, (natures): boolean => {
  if (!natures?.length) {
    return false;
  }

  return natures.some(nature => {
    const entities = [...(nature.spareParts || []), ...(nature.services || [])];

    return entities.some(entity => entity.articles?.some(article => article.gridValueId === POGOS.WARRANTY.masterdataId));
  });
});

export const selectInterventionType = (state: RootState) => state.intervention.interventionType;

export const selectInterventionModalsDisplay = (state: RootState) => state.intervention.modalsDisplay;

export const selectInterventionFooter = (state: RootState) => state.intervention.footer;

export const selectInterventionFooterState = (state: RootState) => state.intervention.footer.state;

export const selectMissingFields = (state: RootState) => state.intervention.missingFields;

export const selectDepositedProduct = (state: RootState) => state.intervention.depositedProduct;

export const selectInterventionFile = (state: RootState) => state.intervention.files;

export const selectInterventionFileNames = (state: RootState) => state.intervention.files.names;

export const selectInterventionFilesList = (state: RootState) => state.intervention.files.list;

export const selectInterventionCreationSite = (state: RootState) => state.intervention.creationSite;

export const selectInterventionRealizationSite = (state: RootState) => state.intervention.realizationSite;

export const selectInterventionWorkshopComment = (state: RootState) => state.intervention.workshopComment;

export const selectInterventionDisableActionButton = (state: RootState) => state.intervention.disable.actionButton;

export const selectStoreComment = (state: RootState) => state.intervention.storeComment;

export const selectIsWarranty = (state: RootState) => state.intervention.isWarranty;

export const selectInterventionRef = (state: RootState) => state.intervention.interventionRef;

export const interventionNotStarted = (state: RootState) =>
  selectSpareParts(state).length === 0 &&
  selectServices(state).length === 0 &&
  (selectIsInterventionStateId(state)(InterventionStatus.inCreation) || selectIsInterventionStateId(state)(InterventionStatus.notStarted));

export const selectIsSparePartOrderInProgress = (articleId: number) => (state: RootState) => {
  const flatSpareParts = selectSpareParts(state).flatMap(x => x.articles);
  return Boolean(flatSpareParts.find(sparePart => sparePart.articleId === articleId)?.orderInProgress);
};

const selectHasSparePartsOrderInProgress = (state: RootState) => {
  const flatSpareParts = selectSpareParts(state).flatMap(x => x.articles);
  return Boolean(flatSpareParts.some(sparePart => sparePart.orderInProgress));
};

const selectIsRealizationSite = createSelector(
  (state: RootState) => state.intervention.realizationSite?.id,
  selectStoreId,
  (realizationSiteId, storeId) => realizationSiteId === storeId
);

const selectIsCreationSite = createSelector(
  (state: RootState) => state.intervention.creationSite?.id,
  selectStoreId,
  (creationSiteId, storeId) => creationSiteId === storeId
);

const selectInterventionButtonMenuFlags = createSelector(
  (state: RootState) => state.intervention.footer.hasInvoice,
  selectIsInternalIntervention,
  (state: RootState) => state.intervention.isToDelete,
  (isInvoice, isInternal, isToDelete) => ({ isInvoice, isInternal, isToDelete })
);

export const selectInterventionButtonMenuState = createSelector(
  selectInterventionStateId,
  selectHasSparePartsOrderInProgress,
  selectInterventionType,
  selectIsRealizationSite,
  selectIsCreationSite,
  selectInterventionButtonMenuFlags,
  (stateId, hasShortage, interventionType, isRealizationSite, isCreationSite, flags) => ({
    stateId,
    hasShortage,
    interventionType,
    isRealizationSite,
    isCreationSite,
    ...flags,
  })
);

export const selectTraceabilitySource = (state: RootState) => state.intervention.traceabilitySource;

export const selectIsBatteryChecked = (state: RootState) => state.intervention.isBatteryChecked;

export const selectPledgeDate = (state: RootState) => state.intervention.footer.pledgeDate;

export const selectBonusRepairCode = (state: RootState) => state.intervention.bonusRepairCode;

export const useSelectHasBatteryProductInStore = () => {
  const isBatteryCheckActivated = useSelectFeatureSettings('isBatteryCheckActivated');
  const categoriesWithBattery = useSelectCategoriesWithBattery();
  return createSelector(selectProduct, product => {
    return isBatteryCheckActivated ? new Set(categoriesWithBattery)?.has(Number(product.category?.id)) : false;
  });
};

export const selectCanBeSentToAPartner = (state: RootState) =>
  new Set([InterventionStatus.toProcess, InterventionStatus.receivedForProcessingInRW]).has(selectInterventionStateId(state));

export const selectIsSentToAPartner = (state: RootState) =>
  new Set([InterventionStatus.deliveredToPartnerFromRw, InterventionStatus.deliveredToPartnerFromStore]).has(selectInterventionStateId(state));

export const selectBusinessType = (state: RootState) => state.intervention.businessType;
